import { useEffect, useState } from "react";
import { Plus, Plugs, ArrowsClockwise, Trash, PencilSimple, CheckCircle, XCircle } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY_FORM = {
    registrar: "godaddy",
    account_name: "",
    label: "",
    api_key: "",
    api_secret: "",
    api_user: "",
    username: "",
    client_ip: "",
    sandbox: false,
    contact_first_name: "",
    contact_last_name: "",
    contact_organization: "",
    contact_email: "",
    contact_phone: "",
    contact_address1: "",
    contact_address2: "",
    contact_city: "",
    contact_state: "",
    contact_postal_code: "",
    contact_country: "",
};

function AccountModal({ open, initial, onClose, onSaved }) {
    const [form, setForm] = useState(EMPTY_FORM);
    const [busy, setBusy] = useState(false);
    const isEdit = !!initial;

    useEffect(() => {
        if (initial) {
            setForm({ ...EMPTY_FORM, ...initial, api_key: "", api_secret: "" });
        } else {
            setForm(EMPTY_FORM);
        }
    }, [initial, open]);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            if (isEdit) {
                const patch = { ...form };
                if (!patch.api_key) delete patch.api_key;
                if (!patch.api_secret) delete patch.api_secret;
                delete patch.id;
                delete patch.registrar; // cannot change type
                delete patch.account_name;
                await api.patch(`/admin/registrar-accounts/${initial.id}`, patch);
                toast.success("Account updated");
            } else {
                await api.post("/admin/registrar-accounts", form);
                toast.success("Account added");
            }
            onSaved();
            onClose();
        } catch (e2) {
            toast.error(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    };

    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(15, 23, 45, 0.35)", backdropFilter: "blur(6px)" }}
            onClick={onClose}
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                className="glass-strong w-full max-w-2xl p-8 max-h-[90vh] overflow-auto"
                data-testid="registrar-account-modal"
            >
                <h2
                    className="text-2xl font-extrabold mb-6"
                    style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                >
                    {isEdit ? "Edit account" : "Add registrar account"}
                </h2>
                <div className="grid grid-cols-2 gap-4">
                    <label className="col-span-1">
                        <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                            Registrar
                        </span>
                        <select
                            data-testid="account-registrar-select"
                            value={form.registrar}
                            onChange={upd("registrar")}
                            disabled={isEdit}
                            className="glass-input"
                        >
                            <option value="godaddy">GoDaddy</option>
                            <option value="namecheap">Namecheap</option>
                        </select>
                    </label>
                    <label className="col-span-1">
                        <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                            Account name
                        </span>
                        <input
                            data-testid="account-name-input"
                            value={form.account_name}
                            onChange={upd("account_name")}
                            disabled={isEdit}
                            placeholder="e.g. gd_new_a"
                            className="glass-input mono"
                            required
                        />
                    </label>
                    <label className="col-span-2">
                        <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                            Label (optional)
                        </span>
                        <input
                            value={form.label || ""}
                            onChange={upd("label")}
                            placeholder="Human-friendly label"
                            className="glass-input"
                        />
                    </label>

                    {form.registrar === "godaddy" ? (
                        <>
                            <label className="col-span-1">
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                                    API Key {isEdit && "(leave blank to keep)"}
                                </span>
                                <input
                                    data-testid="account-api-key-input"
                                    value={form.api_key}
                                    onChange={upd("api_key")}
                                    className="glass-input mono"
                                    required={!isEdit}
                                    type="password"
                                />
                            </label>
                            <label className="col-span-1">
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                                    API Secret {isEdit && "(leave blank to keep)"}
                                </span>
                                <input
                                    data-testid="account-api-secret-input"
                                    value={form.api_secret}
                                    onChange={upd("api_secret")}
                                    className="glass-input mono"
                                    required={!isEdit}
                                    type="password"
                                />
                            </label>
                        </>
                    ) : (
                        <>
                            <label className="col-span-1">
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                                    API User
                                </span>
                                <input
                                    data-testid="account-api-user-input"
                                    value={form.api_user}
                                    onChange={upd("api_user")}
                                    className="glass-input mono"
                                    required
                                />
                            </label>
                            <label className="col-span-1">
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                                    API Key {isEdit && "(leave blank to keep)"}
                                </span>
                                <input
                                    data-testid="account-api-key-input"
                                    value={form.api_key}
                                    onChange={upd("api_key")}
                                    className="glass-input mono"
                                    required={!isEdit}
                                    type="password"
                                />
                            </label>
                            <label className="col-span-1">
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                                    Username (optional — defaults to API User)
                                </span>
                                <input
                                    value={form.username}
                                    onChange={upd("username")}
                                    className="glass-input mono"
                                />
                            </label>
                            <label className="col-span-1">
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                                    Whitelisted Client IP
                                </span>
                                <input
                                    data-testid="account-client-ip-input"
                                    value={form.client_ip}
                                    onChange={upd("client_ip")}
                                    className="glass-input mono"
                                    required
                                    placeholder="e.g. 12.34.56.78"
                                />
                            </label>
                            <label className="col-span-2 flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={form.sandbox}
                                    onChange={(e) => setForm({ ...form, sandbox: e.target.checked })}
                                />
                                <span className="text-sm">Use Namecheap sandbox</span>
                            </label>
                        </>
                    )}
                </div>

                {form.registrar === "godaddy" && (
                    <div className="mt-6 border-t border-[rgba(15,41,89,0.08)] pt-5">
                        <div className="text-sm font-bold mb-1">Default registrant contact</div>
                        <p className="text-xs text-[color:var(--dm-text-2)] mb-3">
                            Required only if you want to <b>buy domains via API</b> in this
                            account. Used for all four contact roles (registrant / admin / tech /
                            billing) on every purchase. Skip if you only use the manual copy-paste
                            flow.
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                            <label>
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">First name</span>
                                <input value={form.contact_first_name} onChange={upd("contact_first_name")} className="glass-input" data-testid="contact-first-name" />
                            </label>
                            <label>
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">Last name</span>
                                <input value={form.contact_last_name} onChange={upd("contact_last_name")} className="glass-input" data-testid="contact-last-name" />
                            </label>
                            <label className="col-span-2">
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">Organization (optional)</span>
                                <input value={form.contact_organization} onChange={upd("contact_organization")} className="glass-input" />
                            </label>
                            <label>
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">Email</span>
                                <input type="email" value={form.contact_email} onChange={upd("contact_email")} className="glass-input" data-testid="contact-email" />
                            </label>
                            <label>
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">Phone (E.164, e.g. +1.4155551234)</span>
                                <input value={form.contact_phone} onChange={upd("contact_phone")} className="glass-input mono" placeholder="+1.4155551234" data-testid="contact-phone" />
                            </label>
                            <label className="col-span-2">
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">Address line 1</span>
                                <input value={form.contact_address1} onChange={upd("contact_address1")} className="glass-input" data-testid="contact-address1" />
                            </label>
                            <label className="col-span-2">
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">Address line 2 (optional)</span>
                                <input value={form.contact_address2} onChange={upd("contact_address2")} className="glass-input" />
                            </label>
                            <label>
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">City</span>
                                <input value={form.contact_city} onChange={upd("contact_city")} className="glass-input" data-testid="contact-city" />
                            </label>
                            <label>
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">State / Region</span>
                                <input value={form.contact_state} onChange={upd("contact_state")} className="glass-input" data-testid="contact-state" />
                            </label>
                            <label>
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">Postal code</span>
                                <input value={form.contact_postal_code} onChange={upd("contact_postal_code")} className="glass-input" data-testid="contact-postal" />
                            </label>
                            <label>
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">Country (ISO alpha-2, e.g. US)</span>
                                <input value={form.contact_country} onChange={upd("contact_country")} className="glass-input mono uppercase" maxLength={2} data-testid="contact-country" />
                            </label>
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-2 mt-8">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-ghost"
                        data-testid="account-cancel-button"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={busy}
                        className="btn-glossy"
                        data-testid="account-save-button"
                    >
                        {busy ? "Saving…" : isEdit ? "Save" : "Add account"}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default function RegistrarAccountsPage() {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState({ open: false, initial: null });
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/registrar-accounts");
            setAccounts(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const test = async (id) => {
        setBusyId(id);
        try {
            const { data } = await api.post(`/admin/registrar-accounts/${id}/test-connection`);
            if (data.ok) toast.success("Connection OK");
            else toast.error(`Failed: ${data.detail?.error || data.detail?.body || "unknown"}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const sync = async (id) => {
        setBusyId(id);
        try {
            await api.post(`/admin/registrar-accounts/${id}/sync`);
            toast.success("Sync scheduled — refresh in a moment");
            setTimeout(load, 5000);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const syncAll = async () => {
        const active = accounts.filter((a) => a.is_active);
        if (active.length === 0) return;
        setBusyId("__all__");
        try {
            await Promise.all(
                active.map((a) => api.post(`/admin/registrar-accounts/${a.id}/sync`))
            );
            toast.success(`Sync scheduled for ${active.length} account(s)`);
            setTimeout(load, 8000);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const del = async (id, name) => {
        if (!confirm(`Delete ${name}? This will also remove its cached domains.`)) return;
        try {
            await api.delete(`/admin/registrar-accounts/${id}`);
            toast.success("Deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-4" data-testid="registrar-accounts-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1
                        className="text-3xl font-extrabold"
                        style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                    >
                        Registrar accounts
                    </h1>
                    <p className="text-sm text-[color:var(--dm-text-2)] mt-1">
                        Connect every GoDaddy or Namecheap account you own. API credentials are encrypted at rest.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={syncAll}
                        disabled={busyId === "__all__" || accounts.length === 0}
                        className="btn-ghost"
                        data-testid="sync-all-button"
                        title="Trigger a background sync for every active account"
                    >
                        <ArrowsClockwise size={16} weight="duotone" /> Sync all
                    </button>
                    <button
                        onClick={() => setModal({ open: true, initial: null })}
                        className="btn-glossy"
                        data-testid="add-account-button"
                    >
                        <Plus size={16} weight="bold" /> Add account
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="glass p-6 text-sm">Loading…</div>
            ) : accounts.length === 0 ? (
                <div className="glass p-10 text-center">
                    <Plugs size={40} weight="duotone" className="mx-auto text-[color:var(--dm-brand)] mb-3" />
                    <div className="text-lg font-bold mb-1">No accounts yet</div>
                    <div className="text-sm text-[color:var(--dm-text-2)] mb-4">
                        Add your first GoDaddy or Namecheap account to start syncing domains.
                    </div>
                    <button
                        onClick={() => setModal({ open: true, initial: null })}
                        className="btn-glossy"
                    >
                        <Plus size={16} weight="bold" /> Add account
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {accounts.map((a) => (
                        <div
                            key={a.id}
                            className="glass p-5"
                            data-testid={`registrar-account-card-${a.account_name}`}
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <div className="mono font-bold text-lg">{a.account_name}</div>
                                    {a.label && (
                                        <div className="text-xs text-[color:var(--dm-text-2)] mt-0.5">
                                            {a.label}
                                        </div>
                                    )}
                                </div>
                                <span
                                    className={`pill ${
                                        a.registrar === "godaddy" ? "pill-warn" : "pill-brand"
                                    }`}
                                >
                                    {a.registrar}
                                </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-xs mb-4">
                                <div>
                                    <div className="text-[color:var(--dm-text-2)] mb-0.5">Status</div>
                                    <div className="flex items-center gap-1.5">
                                        {a.connection_status === "ok" ? (
                                            <>
                                                <CheckCircle
                                                    size={14}
                                                    weight="duotone"
                                                    className="text-[color:var(--dm-success)]"
                                                />
                                                <span>Connected</span>
                                            </>
                                        ) : a.connection_status === "error" ? (
                                            <>
                                                <XCircle
                                                    size={14}
                                                    weight="duotone"
                                                    className="text-[color:var(--dm-danger)]"
                                                />
                                                <span title={a.connection_error}>Error</span>
                                            </>
                                        ) : (
                                            <span className="text-[color:var(--dm-text-2)]">
                                                Not tested
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[color:var(--dm-text-2)] mb-0.5">
                                        Domains
                                    </div>
                                    <div className="font-bold">{a.domain_count}</div>
                                </div>
                                <div>
                                    <div className="text-[color:var(--dm-text-2)] mb-0.5">API Key</div>
                                    <code className="mono">{a.api_key_masked || "—"}</code>
                                </div>
                                <div>
                                    <div className="text-[color:var(--dm-text-2)] mb-0.5">
                                        Last synced
                                    </div>
                                    <div className="mono">
                                        {a.last_synced_at
                                            ? new Date(a.last_synced_at).toLocaleString()
                                            : "never"}
                                    </div>
                                </div>
                                {a.registrar === "namecheap" && (
                                    <>
                                        <div>
                                            <div className="text-[color:var(--dm-text-2)] mb-0.5">
                                                API User
                                            </div>
                                            <code className="mono">{a.api_user}</code>
                                        </div>
                                        <div>
                                            <div className="text-[color:var(--dm-text-2)] mb-0.5">
                                                Client IP
                                            </div>
                                            <code className="mono">{a.client_ip}</code>
                                        </div>
                                    </>
                                )}
                            </div>
                            {a.connection_error && (
                                <div className="text-xs text-[color:var(--dm-danger)] mb-3 bg-[rgba(255,59,48,0.06)] p-2 rounded">
                                    {a.connection_error}
                                </div>
                            )}
                            <div className="flex flex-wrap gap-2">
                                <button
                                    className="btn-ghost text-xs"
                                    onClick={() => test(a.id)}
                                    disabled={busyId === a.id}
                                    data-testid={`test-connection-${a.account_name}`}
                                >
                                    {busyId === a.id ? "Testing…" : "Test"}
                                </button>
                                <button
                                    className="btn-ghost text-xs"
                                    onClick={() => sync(a.id)}
                                    disabled={busyId === a.id}
                                    data-testid={`sync-account-${a.account_name}`}
                                >
                                    <ArrowsClockwise size={14} weight="duotone" /> Sync domains
                                </button>
                                <button
                                    className="btn-ghost text-xs"
                                    onClick={() => setModal({ open: true, initial: a })}
                                    data-testid={`edit-account-${a.account_name}`}
                                >
                                    <PencilSimple size={14} weight="duotone" /> Edit
                                </button>
                                <button
                                    className="btn-ghost btn-danger text-xs"
                                    onClick={() => del(a.id, a.account_name)}
                                    data-testid={`delete-account-${a.account_name}`}
                                >
                                    <Trash size={14} weight="duotone" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <AccountModal
                open={modal.open}
                initial={modal.initial}
                onClose={() => setModal({ open: false, initial: null })}
                onSaved={load}
            />
        </div>
    );
}
